import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import SearchIcon from '@mui/icons-material/Search';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { useNavigate } from 'react-router-dom';

import { useGetAllUserStandardsQuery } from '../standardsCatalogApi';
import {
  buildUserStandardsTree,
  collectStandardMatches,
  getCollections,
  type UserStandardTreeNode,
} from '../utils/userStandardsTree';
import { EmptyState, SkeletonRows } from '../components/atoms';
import { CollectionsPanel } from '../components/library/CollectionsPanel';
import { LibraryDndProvider } from '../components/library/dnd/LibraryDndProvider';
import { LibraryTreeDnd } from '../components/library/dnd/LibraryTreeDnd';
import { LibraryNodeRow } from '../components/library/LibraryNodeRow';
import { LibraryDetailsPanel } from '../components/library/LibraryDetailsPanel';
import { CreateNodeDialog } from '../components/library/CreateNodeDialog';
import { MoveToDialog } from '../components/library/MoveToDialog';
import { STANDARDS_FIND_PATH } from '../paths';

import type { UUID } from '@/shared/types/userStandard';
import { useAuth } from '@/shared/components/AuthProvider';

interface CreateState {
  open: boolean;
  parentId: UUID | null;
  type: 'folder' | 'standard';
}

const PANEL_SX = { height: { xs: 'auto', md: 'calc(100vh - 240px)' }, minHeight: 520 } as const;
const COLLECTIONS_WIDTH = 248;
const COLLECTIONS_COLLAPSED_WIDTH = 52;

export default function MyLibraryView() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const ownerId = user?.id;
  // The scrollable tree pane — Pragmatic auto-scroll and the post-move flash query against it.
  const [treeScrollElement, setTreeScrollElement] = useState<HTMLDivElement | null>(null);
  const handleTreeScrollRef = useCallback((element: HTMLDivElement | null) => {
    setTreeScrollElement(element);
  }, []);

  const { data: rows = [], isLoading } = useGetAllUserStandardsQuery(
    { ownerId: ownerId ?? '' },
    { skip: !ownerId }
  );

  // Folders start collapsed; the user expands what they need.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // The collection that scopes the middle tree. `null` = "All collections" (unscoped); `undefined`
  // = the user hasn't chosen yet, so we fall back to a sensible default during render.
  const [chosenCollectionId, setChosenCollectionId] = useState<UUID | null | undefined>(undefined);
  const [collectionsCollapsed, setCollectionsCollapsed] = useState(false);
  const [create, setCreate] = useState<CreateState>({
    open: false,
    parentId: null,
    type: 'standard',
  });
  const [moveNode, setMoveNode] = useState<UserStandardTreeNode | null>(null);
  const [error, setError] = useState<string | null>(null);

  const roots = useMemo(() => buildUserStandardsTree(rows), [rows]);
  const collections = useMemo(() => getCollections(roots), [roots]);

  // Resolve the active collection from the user's choice, derived during render so there are no
  // state-syncing effects: honor an explicit pick (dropping a stale id if its collection was
  // deleted); otherwise default to the first collection that has content on desktop (so we never
  // land on an empty one), falling back to "All collections" — which shows the whole library.
  const activeCollectionId: UUID | null = useMemo(() => {
    if (chosenCollectionId !== undefined) {
      if (chosenCollectionId && !collections.some((c) => c.id === chosenCollectionId)) return null;
      return chosenCollectionId;
    }
    if (!isNarrow) {
      const firstWithContent = collections.find((c) => c.children.length > 0);
      if (firstWithContent) return firstWithContent.id;
    }
    return null;
  }, [chosenCollectionId, collections, isNarrow]);

  const activeCollection = useMemo(
    () =>
      activeCollectionId ? (collections.find((c) => c.id === activeCollectionId) ?? null) : null,
    [collections, activeCollectionId]
  );
  const treeRoots = activeCollection ? activeCollection.children : [];

  const selectedNode = useMemo<UserStandardTreeNode | null>(() => {
    if (!selectedId) return null;
    const row = rows.find((r) => r.id === selectedId);
    return row ? { ...row, children: [] } : null;
  }, [rows, selectedId]);

  const matches = useMemo(
    () => (search.trim() ? collectStandardMatches(rows, search, activeCollection) : null),
    [rows, search, activeCollection]
  );

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openCreate = (parentId: UUID | null, type: 'folder' | 'standard') =>
    setCreate({ open: true, parentId, type });

  const handleSelectCollection = (id: UUID | null) => {
    setChosenCollectionId(id);
    setSearch('');
    // Desktop: also surface the collection in the details pane (rename/add/delete). Mobile drills
    // into the tree screen instead.
    setSelectedId(!isNarrow && id ? id : null);
  };

  const handleSearchAll = () => setChosenCollectionId(null);
  const backToCollections = () => {
    // Reset to the unchosen state so the mobile drill-down returns to the collections list.
    setChosenCollectionId(undefined);
    setSearch('');
  };
  const backToTree = () => setSelectedId(null);

  const detailsPanel = ownerId ? (
    <LibraryDetailsPanel
      node={selectedNode}
      ownerId={ownerId}
      onAddChild={openCreate}
      onMove={setMoveNode}
      onAfterDelete={() => setSelectedId(null)}
      onError={setError}
      onClose={isNarrow ? undefined : () => setSelectedId(null)}
    />
  ) : null;

  if (!ownerId) {
    return (
      <Paper variant="outlined" sx={PANEL_SX}>
        <SkeletonRows />
      </Paper>
    );
  }

  const treeArea = isLoading ? (
    <SkeletonRows count={8} />
  ) : rows.length === 0 ? (
    <EmptyState
      icon={<LibraryBooksIcon />}
      title="You haven't added any standards yet"
      description="Create a collection, then add custom standards or import them from a source."
      actions={
        <>
          <Button
            variant="outlined"
            startIcon={<CreateNewFolderOutlinedIcon />}
            onClick={() => openCreate(null, 'folder')}
          >
            New collection
          </Button>
          <Button
            variant="contained"
            startIcon={<TravelExploreIcon />}
            onClick={() => navigate(STANDARDS_FIND_PATH)}
          >
            Import from sources
          </Button>
        </>
      }
    />
  ) : matches ? (
    <Box sx={{ p: 1 }}>
      {activeCollectionId && (
        <Box sx={{ px: 1, pb: 1 }}>
          <Button size="small" onClick={handleSearchAll}>
            Search all collections
          </Button>
        </Box>
      )}
      {matches.length === 0 ? (
        <EmptyState
          title="No matches"
          description={`Nothing ${
            activeCollection ? `in ${activeCollection.title}` : 'in your standards'
          } matches “${search}”.`}
        />
      ) : (
        matches.map((node) => (
          <LibraryNodeRow
            key={node.id}
            node={node}
            depth={0}
            dragHandle={null}
            expanded={false}
            selected={selectedId === node.id}
            onToggleExpand={() => undefined}
            onSelect={(n) => setSelectedId(n.id)}
          />
        ))
      )}
    </Box>
  ) : activeCollection ? (
    treeRoots.length === 0 ? (
      <EmptyState
        icon={<FolderOutlinedIcon />}
        title="This collection is empty"
        description="Add a standard or folder, or import standards from a source."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<NoteAddOutlinedIcon />}
              onClick={() => openCreate(activeCollection.id, 'standard')}
            >
              Add standard
            </Button>
            <Button
              variant="outlined"
              startIcon={<CreateNewFolderOutlinedIcon />}
              onClick={() => openCreate(activeCollection.id, 'folder')}
            >
              Add folder
            </Button>
          </>
        }
      />
    ) : (
      <LibraryTreeDnd
        roots={treeRoots}
        expanded={expanded}
        selectedId={selectedId}
        dragEnabled={!isNarrow}
        onToggleExpand={toggleExpand}
        onSelect={(n) => setSelectedId(n.id)}
      />
    )
  ) : (
    // "All collections": show the whole library so nothing is ever hidden (collections, plus any
    // loose root-level standards from before the redesign). Pick a collection on the left to focus.
    <LibraryTreeDnd
      roots={roots}
      expanded={expanded}
      selectedId={selectedId}
      dragEnabled={!isNarrow}
      onToggleExpand={toggleExpand}
      onSelect={(n) => setSelectedId(n.id)}
    />
  );

  const collectionsPanel = (
    <CollectionsPanel
      collections={collections}
      activeCollectionId={activeCollectionId}
      collapsed={!isNarrow && collectionsCollapsed}
      onSelect={handleSelectCollection}
      onNewCollection={() => openCreate(null, 'folder')}
      onToggleCollapse={isNarrow ? undefined : () => setCollectionsCollapsed((v) => !v)}
    />
  );

  const treePaneHeader = (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        minHeight: 52,
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {isNarrow && (
        <IconButton size="small" onClick={backToCollections} aria-label="Back to collections">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      )}
      <FolderOutlinedIcon fontSize="small" color="action" sx={{ flex: 'none' }} />
      <Typography variant="subtitle1" noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}>
        {activeCollection ? activeCollection.title : 'All collections'}
      </Typography>
      {activeCollection && (
        <>
          <Tooltip title="Add standard to this collection">
            <IconButton
              size="small"
              onClick={() => openCreate(activeCollection.id, 'standard')}
              aria-label="Add standard"
            >
              <NoteAddOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add folder to this collection">
            <IconButton
              size="small"
              onClick={() => openCreate(activeCollection.id, 'folder')}
              aria-label="Add folder"
            >
              <CreateNewFolderOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );

  // On mobile, an explicit pick (a collection or "All collections") or an active search drills into
  // the tree screen; otherwise we sit on the collections list.
  const hasChosenCollection = chosenCollectionId !== undefined;
  const mobileScreen: 'collections' | 'tree' | 'details' = selectedNode
    ? 'details'
    : hasChosenCollection || search.trim()
      ? 'tree'
      : 'collections';

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2, alignItems: { sm: 'center' } }}
      >
        <Button
          variant="contained"
          startIcon={<CreateNewFolderOutlinedIcon />}
          onClick={() => openCreate(null, 'folder')}
        >
          New collection
        </Button>
        <Button
          variant="outlined"
          startIcon={<TravelExploreIcon />}
          onClick={() => navigate(STANDARDS_FIND_PATH)}
        >
          Import from sources
        </Button>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={
            activeCollection ? `Search ${activeCollection.title}` : 'Search all collections'
          }
          sx={{ width: { xs: '100%', sm: 280 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <LibraryDndProvider
        ownerId={ownerId}
        roots={roots}
        rootParentId={activeCollection ? activeCollection.id : null}
        scrollContainerElement={treeScrollElement}
        onError={setError}
      >
        {isNarrow ? (
          <Paper
            variant="outlined"
            sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', ...PANEL_SX }}
          >
            {mobileScreen === 'collections' && collectionsPanel}
            {mobileScreen === 'tree' && (
              <>
                {treePaneHeader}
                <Box ref={handleTreeScrollRef} sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
                  {treeArea}
                </Box>
              </>
            )}
            {mobileScreen === 'details' && (
              <>
                <Box
                  sx={{
                    px: 1,
                    py: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <IconButton size="small" onClick={backToTree} aria-label="Back">
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="subtitle2" color="text.secondary">
                    Details
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>{detailsPanel}</Box>
              </>
            )}
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ display: 'flex', overflow: 'hidden', ...PANEL_SX }}>
            <Box
              sx={{
                width: collectionsCollapsed ? COLLECTIONS_COLLAPSED_WIDTH : COLLECTIONS_WIDTH,
                flex: 'none',
                borderRight: 1,
                borderColor: 'divider',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                transition: 'width .15s ease',
              }}
            >
              {collectionsPanel}
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRight: selectedNode ? 1 : 0,
                borderColor: 'divider',
              }}
            >
              {treePaneHeader}
              <Box ref={handleTreeScrollRef} sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
                {treeArea}
              </Box>
            </Box>
            {selectedNode && (
              <Box sx={{ width: 360, flex: 'none', overflow: 'auto' }}>{detailsPanel}</Box>
            )}
          </Paper>
        )}
      </LibraryDndProvider>

      <CreateNodeDialog
        open={create.open}
        ownerId={ownerId}
        parentId={create.parentId}
        defaultType={create.type}
        onClose={() => setCreate((prev) => ({ ...prev, open: false }))}
      />

      {moveNode && (
        <MoveToDialog
          open={Boolean(moveNode)}
          ownerId={ownerId}
          node={moveNode}
          onClose={() => setMoveNode(null)}
        />
      )}
    </>
  );
}
